import { Link } from 'react-router-dom'
import { Shield, Lock, Database, Eye, ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { SEOHead } from '@/components/SEOHead'

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <SEOHead
        title="Privacy Policy | Sycek OSINT"
        description="Sycek OSINT privacy policy — how we collect, use, and protect your data on our intelligence platform."
        canonical="/privacy-policy"
      />
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 via-purple-900/20 to-red-900/20"></div>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f61a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f61a_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      {/* Header */}
      <div className="relative z-10 border-b border-cyan-500/30 bg-black/90 backdrop-blur-sm shadow-[0_4px_20px_rgba(34,211,238,0.15)]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <img 
              src="/logo.jpeg" 
              alt="Sycek Logo" 
              className="h-10 w-10 object-contain drop-shadow-[0_0_15px_rgba(34,197,94,0.6)]"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <div>
              <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent drop-shadow-[0_0_10px_rgba(34,211,238,0.6)] font-mono">
                SYCEK OSINT
              </h1>
              <p className="text-xs text-green-400 font-mono drop-shadow-[0_0_5px_rgba(34,197,94,0.6)]">[SYSTEM ACTIVE]</p>
            </div>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-cyan-400 hover:bg-cyan-500/20 border border-cyan-500/50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 py-12 max-w-4xl">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <Shield className="w-8 h-8 text-green-400" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-green-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
          </div>
          <p className="text-gray-400 text-sm font-mono">
            Last updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        <Card className="bg-gray-900/50 border border-cyan-500/30 backdrop-blur-sm">
          <CardContent className="p-8 space-y-6">
            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Eye className="w-6 h-6 mr-2" />
                1. Information We Collect
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  At Sycek OSINT, we collect and process information necessary to provide our cyber intelligence services:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-cyan-400">Account Information:</strong> Email address, name, organization details, subscription tier, and authentication credentials</li>
                  <li><strong className="text-cyan-400">Usage Data:</strong> Query logs, API usage statistics, search terms, and investigation activities</li>
                  <li><strong className="text-cyan-400">Technical Data:</strong> IP addresses, device information, browser type, and access timestamps</li>
                  <li><strong className="text-cyan-400">Payment Information:</strong> Processed securely through third-party payment processors (we do not store full card details)</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Database className="w-6 h-6 mr-2" />
                2. How We Use Your Information
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>We use collected information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide and maintain our OSINT investigation platform</li>
                  <li>Process your queries and deliver intelligence results</li>
                  <li>Manage your account, subscriptions, and billing</li>
                  <li>Monitor system performance and prevent abuse</li>
                  <li>Send important service updates and security notifications</li>
                  <li>Comply with legal obligations and enforce our terms</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center">
                <Lock className="w-6 h-6 mr-2" />
                3. Data Security
              </h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  We implement industry-standard security measures to protect your data:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-green-400">Encryption:</strong> All data in transit uses TLS 1.3 encryption</li>
                  <li><strong className="text-green-400">Access Controls:</strong> Role-based access control and authentication requirements</li>
                  <li><strong className="text-green-400">Audit Logging:</strong> Comprehensive logging of all access and modifications</li>
                  <li><strong className="text-green-400">Regular Security Audits:</strong> Continuous monitoring and vulnerability assessments</li>
                  <li><strong className="text-green-400">Data Residency:</strong> Your data is stored in secure, compliant data centers</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">4. Data Sharing and Disclosure</h2>
              <div className="text-gray-300 space-y-3">
                <p>We do not sell your personal information. We may share data only in these circumstances:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-yellow-400">Service Providers:</strong> Trusted third-party services for infrastructure, payment processing, and analytics (under strict confidentiality agreements)</li>
                  <li><strong className="text-yellow-400">Legal Requirements:</strong> When required by law, court order, or government request</li>
                  <li><strong className="text-yellow-400">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets (with notice to users)</li>
                  <li><strong className="text-yellow-400">Consent:</strong> With your explicit permission for specific purposes</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">5. Your Rights</h2>
              <div className="text-gray-300 space-y-3">
                <p>You have the right to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-cyan-400">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-cyan-400">Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong className="text-cyan-400">Deletion:</strong> Request deletion of your account and data</li>
                  <li><strong className="text-cyan-400">Portability:</strong> Export your data in a machine-readable format</li>
                  <li><strong className="text-cyan-400">Objection:</strong> Object to certain processing activities</li>
                  <li><strong className="text-cyan-400">Restriction:</strong> Request limitation of data processing</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, contact us at: <a href="mailto:privacy@sycek.io" className="text-cyan-400 hover:underline">privacy@sycek.io</a>
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">6. Data Retention</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  We retain your data only as long as necessary to provide services and comply with legal obligations:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Active Accounts:</strong> Data retained while account is active</li>
                  <li><strong>Deactivated Accounts:</strong> Data retained for up to 90 days after deactivation</li>
                  <li><strong>Legal Requirements:</strong> Certain data may be retained longer for compliance purposes</li>
                  <li><strong>Backup Data:</strong> Encrypted backups retained for up to 1 year</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">7. Cookies and Tracking</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  We use cookies and similar technologies for:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Authentication and session management</li>
                  <li>Security and fraud prevention</li>
                  <li>Analytics and performance monitoring</li>
                  <li>Personalization of user experience</li>
                </ul>
                <p className="mt-4">
                  You can manage cookie preferences through your browser settings.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">8. International Data Transfers</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  Your data may be transferred and processed in countries outside your jurisdiction. 
                  We ensure appropriate safeguards are in place, including:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Standard contractual clauses approved by data protection authorities</li>
                  <li>Compliance with applicable data protection laws (GDPR, CCPA, etc.)</li>
                  <li>Regular security assessments of international data processing</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">9. Children's Privacy</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  Our services are not intended for individuals under 18 years of age. 
                  We do not knowingly collect personal information from children. 
                  If you believe we have collected such information, please contact us immediately.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">10. Changes to This Policy</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  We may update this Privacy Policy periodically. Material changes will be communicated via:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Email notification to registered users</li>
                  <li>Prominent notice on our platform</li>
                  <li>Updated "Last updated" date at the top of this page</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">11. Contact Us</h2>
              <div className="text-gray-300 space-y-3">
                <p>
                  For questions, concerns, or requests regarding this Privacy Policy or our data practices:
                </p>
                <div className="bg-gray-800/50 border border-cyan-500/30 rounded-lg p-4 font-mono">
                  <p><strong className="text-cyan-400">Email:</strong> privacy@sycek.io</p>
                  <p><strong className="text-cyan-400">Data Protection Officer:</strong> dpo@sycek.io</p>
                  <p><strong className="text-cyan-400">Mailing Address:</strong> Sycek OSINT, Bangalore KA, INDIA</p>
                </div>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

